import React, { useState } from "react";
import PropTypes from "prop-types";
import { FiX, FiAlertCircle, FiCheck } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./InlineAlert.scss";

const ICONS = {
	error: FiX,
	warning: FiAlertCircle,
	info: FiAlertCircle,
	success: FiCheck,
};

const InlineAlert = ({
	variant = "error",
	layout = "inline",
	title,
	message,
	children,
	details,
	detailsLabel,
	detailsOpen = false,
	actionLabel,
	onAction,
	onDismiss,
	className = "",
	role,
}) => {
	const { t } = useTranslation();
	const [showDetails, setShowDetails] = useState(detailsOpen);
	const content = message ?? children;
	const hasDetails = Boolean(details);
	const resolvedDetailsLabel = detailsLabel ?? t("common.viewDetails");
	const resolvedRole = role ?? (variant === "error" ? "alert" : "status");
	const Icon = ICONS[variant] || FiX;

	if (!content && !title && !details) {
		return null;
	}

	return (
		<div
			className={`inline-alert inline-alert--${variant} inline-alert--${layout} ${className}`.trim()}
			role={resolvedRole}
		>
			<span className="inline-alert__icon" aria-hidden>
				<Icon />
			</span>

			<div className="inline-alert__body">
				{title && layout === "banner" && (
					<div className="inline-alert__title">{title}</div>
				)}

				{content && (
					<div className="inline-alert__content">
						<span className="inline-alert__message">{content}</span>
						{hasDetails && !showDetails && (
							<button
								type="button"
								className="inline-alert__details-link"
								onClick={() => setShowDetails(true)}
							>
								{resolvedDetailsLabel}
							</button>
						)}
					</div>
				)}

				{hasDetails && showDetails && (
					<div className="inline-alert__details">{details}</div>
				)}
			</div>

			{layout === "banner" && (actionLabel || onDismiss) && (
				<div className="inline-alert__actions">
					{actionLabel && onAction && (
						<button
							type="button"
							className="inline-alert__action btn"
							onClick={onAction}
						>
							{actionLabel}
						</button>
					)}
					{onDismiss && (
						<button
							type="button"
							className="inline-alert__dismiss btn btn-transparent"
							onClick={onDismiss}
							aria-label={t("common.close")}
						>
							<FiX />
						</button>
					)}
				</div>
			)}
		</div>
	);
};

InlineAlert.propTypes = {
	variant: PropTypes.oneOf(["error", "warning", "info", "success"]),
	layout: PropTypes.oneOf(["inline", "banner"]),
	title: PropTypes.node,
	message: PropTypes.node,
	children: PropTypes.node,
	details: PropTypes.node,
	detailsLabel: PropTypes.string,
	detailsOpen: PropTypes.bool,
	actionLabel: PropTypes.string,
	onAction: PropTypes.func,
	onDismiss: PropTypes.func,
	className: PropTypes.string,
	role: PropTypes.string,
};

export default InlineAlert;
